import { IonSelect, IonSelectOption } from '@ionic/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useStorage } from '../../../hooks/Utils/useStorage';
import './Sorter.scss';
import { SorterProps } from './types';

export type SortCriterion<T> = {
  key: string;
  label: string;
  compare: (a: T, b: T) => number;
  filter?: (item: T) => boolean; // Si está presente, filtra los elementos que no cumplan la condición
};

const Sorter = <T,>({
  items,
  criteria,
  defaultKey,
  label = 'Ordenar por',
  storageKey,
  hideEmptyCriteria,
  onSortKeyChange,
  children,
}: SorterProps<T>) => {
  const { getValue, setValue } = useStorage();

  // Filtrar criterios que no tienen items (si hideEmptyCriteria está activado)
  const availableCriteria = useMemo(() => {
    if (!hideEmptyCriteria || !items?.length) return criteria;

    return criteria.filter((criterion) => {
      // Criterios sin filtro siempre se muestran (ej: "Todos")
      if (!criterion.filter) return true;
      // Solo mostrar si hay al menos un item que cumpla el filtro
      return items.some(criterion.filter);
    });
  }, [criteria, items, hideEmptyCriteria]);

  const handleSelectClick = useCallback(() => {
    (document.activeElement as HTMLElement)?.blur();
  }, []);

  const [sortKey, setSortKey] = useState(defaultKey ?? availableCriteria[0]?.key);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (sortKey) {
      onSortKeyChange?.(sortKey);
    }
  }, [sortKey, onSortKeyChange]);

  // Cargar valor persistido del storage
  useEffect(() => {
    if (!storageKey) {
      requestAnimationFrame(() => setIsMounted(true));
      return;
    }

    getValue(storageKey).then((storedValue) => {
      if (storedValue && availableCriteria.some((c) => c.key === storedValue)) {
        setSortKey(storedValue);
      }
      requestAnimationFrame(() => setIsMounted(true));
    });
  }, [storageKey, availableCriteria, getValue]);

  // Si el criterio seleccionado ya no está disponible, cambiar al primero disponible
  useEffect(() => {
    if (sortKey && !availableCriteria.some((c) => c.key === sortKey)) {
      const newKey = availableCriteria[0]?.key;
      if (newKey) {
        setSortKey(newKey);
        if (storageKey) setValue(storageKey, newKey);
      }
    }
  }, [sortKey, availableCriteria, storageKey, setValue]);

  // Handler para cambio de criterio
  const handleSortChange = useCallback(
    (newKey: string) => {
      setSortKey(newKey);
      if (storageKey) {
        setValue(storageKey, newKey);
      }
    },
    [storageKey, setValue],
  );

  const sortedItems = useMemo(() => {
    if (!items) return [];

    const criterion = availableCriteria.find((c) => c.key === sortKey);
    if (!criterion) return items;

    // Si el criterio tiene un filtro, aplicarlo primero
    const filteredItems = criterion.filter
      ? items.filter(criterion.filter)
      : items;

    // Luego ordenar los elementos (filtrados o no)
    return [...filteredItems].sort(criterion.compare);
  }, [items, sortKey, availableCriteria]);

  return (
    <div className="sorter">
      <div className="sorter__header" onClick={handleSelectClick}>
        <span className="sorter__label">{label}</span>

        {isMounted && (
          <IonSelect
            value={sortKey}
            interface="action-sheet"
            onIonChange={(e) => handleSortChange(e.detail.value)}
            className="sorter__select"
            cancelText="Cancelar"
          >
            {availableCriteria.map((criterion) => (
              <IonSelectOption key={criterion.key} value={criterion.key}>
                {criterion.label}
              </IonSelectOption>
            ))}
          </IonSelect>
        )}
      </div>

      <div className="sorter__children">{children(sortedItems)}</div>
    </div>
  );
};

export default React.memo(Sorter) as typeof Sorter;
